import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services';

export default function useReactionChain(reaction, enabled) {
  const [state, setState] = useState({ list: null, next: null, loading: false, error: false });
  const version = useRef(0);
  const pending = useRef(false);
  const controller = useRef(null);
  const requestedOffset = useRef(0);
  const initial = useRef(reaction.quote);
  const load = useCallback(async (offset = 0) => {
    if (pending.current) return;
    const generation = version.current;
    pending.current = true;
    const request = new AbortController();
    controller.current = request;
    requestedOffset.current = offset;
    setState(previous => ({ ...previous, loading: true, error: false }));
    try {
      const response = await api.reactionQuotes({ id: reaction.id, offset, limit: 20 }, { signal: request.signal });
      if (generation !== version.current) return;
      if (!response.success || !Array.isArray(response.list)) throw new Error('Chain unavailable');
      setState(previous => {
        const seen = new Set();
        const list = [...(offset ? previous.list || [] : []), ...response.list]
          .filter(item => item?.id && item.id !== reaction.id && !seen.has(item.id) && seen.add(item.id));
        return { list, next: response.next_offset, loading: false, error: false };
      });
    } catch {
      if (generation === version.current) setState(previous => ({ ...previous, loading: false, error: true }));
    } finally {
      if (generation === version.current) pending.current = false;
      if (controller.current === request) controller.current = null;
    }
  }, [reaction.id]);
  useEffect(() => {
    if (enabled && initial.current) load(0);
    return () => { version.current += 1; pending.current = false; controller.current?.abort(); };
  }, [enabled, load]);
  return { ...state, loaded: state.list !== null, list: state.list ?? (reaction.quote ? [reaction.quote] : []),
    loadMore: () => load(state.next ?? 0), retry: () => load(requestedOffset.current) };
}
